import React, { useState } from 'react';
import { motion } from 'framer-motion';
import '../styles/Blog.css';

function Blog() {
  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHillStation, setSelectedHillStation] = useState(null);

  // Hill stations data
  const hillStations = [
    {
      name: 'Ooty',
      image: '/assets/images/ooty.png',
      description: 'Known as the Queen of Hill Stations, Ooty offers lush tea gardens and serene lakes.',
      distance: '85 km from Coimbatore',
      attractions: [
        {
          name: 'Ooty Lake',
          description: 'A serene lake perfect for boating, surrounded by eucalyptus trees.',
          highlights: 'Boat rides, scenic views, nearby tea estates.'
        },
        {
          name: 'Botanical Garden',
          description: 'A 55-acre garden showcasing exotic plants and flowers.',
          highlights: 'Rare flora, glasshouse, annual flower show.'
        },
        {
          name: 'Nilgiri Mountain Railway',
          description: 'A UNESCO World Heritage Site offering a scenic toy train ride.',
          highlights: 'Historic train, stunning Nilgiri views.'
        }
      ]
    },
    {
      name: 'Coonoor',
      image: '/assets/images/conoor.png',
      description: 'Famous for its tea plantations and tranquil landscapes.',
      distance: '68 km from Coimbatore',
      attractions: [
        {
          name: 'Sim’s Park',
          description: 'A beautifully maintained park with vibrant flowers and trees.',
          highlights: 'Rare plants, boating, scenic walks.'
        },
        {
          name: 'Dolphin’s Nose',
          description: 'A viewpoint offering panoramic views of the Nilgiris.',
          highlights: 'Stunning vistas, tea estates, trekking.'
        },
        {
          name: 'Lamb’s Rock',
          description: 'A scenic spot with views of the Coimbatore plains.',
          highlights: 'Nature trails, photography, peaceful ambiance.'
        }
      ]
    },
    {
      name: 'Kotagiri',
      image: '/assets/images/kotagiri.png',
      description: 'A quaint hill station with serene tea estates and misty hills.',
      distance: '70 km from Coimbatore',
      attractions: [
        {
          name: 'Kodanad Viewpoint',
          description: 'Offers breathtaking views of the Nilgiri plains and rivers.',
          highlights: 'Sunrise views, trekking, photography.'
        },
        {
          name: 'Catherine Falls',
          description: 'A stunning double-cascaded waterfall amidst lush greenery.',
          highlights: 'Scenic beauty, short trek, picnic spot.'
        },
        {
          name: 'Rangaswami Peak',
          description: 'A sacred peak ideal for trekking and spiritual retreats.',
          highlights: 'Challenging trek, panoramic views.'
        }
      ]
    },
    {
      name: 'Valparai',
      image: '/assets/images/valparai.png',
      description: 'A hidden gem with lush forests and cascading waterfalls.',
      distance: '105 km from Coimbatore',
      attractions: [
        {
          name: 'Sholayar Dam',
          description: 'A serene dam surrounded by tea plantations and forests.',
          highlights: 'Boating, scenic views, wildlife spotting.'
        },
        {
          name: 'Nallamudi Viewpoint',
          description: 'Offers stunning views of the Anamalai Hills and valleys.',
          highlights: 'Tea estates, hiking, photography.'
        },
        {
          name: 'Monkey Falls',
          description: 'A refreshing waterfall ideal for picnics and trekking.',
          highlights: 'Cool waters, forest trails, family-friendly.'
        }
      ]
    },
    {
      name: 'Kodaikanal',
      image: '/assets/images/kodaikanal.png',
      description: 'The Princess of Hill Stations, known for its lakes and greenery.',
      distance: '177 km from Coimbatore',
      attractions: [
        {
          name: 'Kodai Lake',
          description: 'A star-shaped lake perfect for boating and cycling.',
          highlights: 'Boat rides, horse riding, serene ambiance.'
        },
        {
          name: 'Coaker’s Walk',
          description: 'A scenic walkway with panoramic valley views.',
          highlights: 'Sunset views, nature walks, photography.'
        },
        {
          name: 'Pillar Rocks',
          description: 'Majestic rock formations shrouded in mist.',
          highlights: 'Unique geology, viewpoints, trekking.'
        }
      ]
    },
    
  ];

  // Animation variants
  const sectionVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: 'easeOut' } },
    exit: { opacity: 0, scale: 0.8, transition: { duration: 0.2, ease: 'easeIn' } }
  };

  // Modal open/close handlers
  const openModal = (hillStation) => {
    setSelectedHillStation(hillStation);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedHillStation(null);
  };

  return (
    <main className="blog-main">
      {/* Hero Section */}
      <section className="blog-hero-section">
       <div className="blog-hero-content">
  <h1 className="blog-hero-title">
    Top Places to Explore on Your Next Adventure
  </h1>
  <p className="blog-hero-subtitle">
    Discover exciting destinations around the world — from vibrant cities to hidden gems.
  </p>
</div>
      </section>

      {/* Hill Stations Section */}
      <motion.section
        className="hill-stations-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={sectionVariants}
      >
        <div className="section-container">
          <h2 className="section-title">Explore Nearby Hill Stations</h2>
          <div className="hill-stations-grid">
            {hillStations.map((hillStation, index) => (
              <motion.div
                key={index}
                className="hill-station-card"
                variants={cardVariants}
                whileHover={{ scale: 1.03 }}
              >
                <img
                  src={hillStation.image}
                  alt={hillStation.name}
                  className="hill-station-image"
                />
                <div className="hill-station-content">
                  <h3 className="hill-station-title">{hillStation.name}</h3>
                  <p className="hill-station-description">{hillStation.description}</p>
                  <p className="hill-station-distance">{hillStation.distance}</p>
                  <motion.button
                    className="hill-station-button hover-scale"
                    onClick={() => openModal(hillStation)}
                    whileHover={{ scale: 1.03 }}
                  >
                    View Attractions
                  </motion.button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* Modal */}
      {isModalOpen && selectedHillStation && (
        <motion.div
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={closeModal}
        >
          <motion.div
            className="modal-content"
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="modal-title">Top Attractions in {selectedHillStation.name}</h2>
            <div className="modal-attractions">
              {selectedHillStation.attractions.map((attraction, index) => (
                <div key={index} className="attraction-item">
                  <h3 className="attraction-title">{attraction.name}</h3>
                  <p className="attraction-description">{attraction.description}</p>
                  <p className="attraction-highlights">
                    <strong>Highlights:</strong> {attraction.highlights}
                  </p>
                </div>
              ))}
            </div>
            <motion.button
              className="modal-close-button hover-scale"
              onClick={closeModal}
              whileHover={{ scale: 1.03 }}
            >
              Close
            </motion.button>
          </motion.div>
        </motion.div>
      )}
    </main>
  );
}

export default Blog;